import { useState } from 'react';
import { Dices, Sparkles, X, Wand2, Loader2, AlertCircle, RotateCcw, ScanLine, ShieldCheck, FlaskConical, Save, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';
import { RegionSelector } from '@/components/ui/RegionSelector';
import { supabase } from '@/lib/supabase';
import { findUnknownPlaceholders } from '@/lib/promptPlaceholders';
import type { StageRefinementPreview } from '@/services/geminiService';
import type { BoxCoordinates } from '@/types';

export interface RefinementOverride {
  finalPrompt: string;
  scope: 'global' | 'local';
  intent: 'cosmetic' | 'structural';
}

/** Options the modal can pass to a dry-run analysis (admin stage-prompt tuning). */
export interface AnalyzeOptions {
  classifierPromptOverride?: string;
  rewritePromptOverride?: string;
  /** Which per-case rewrite prompt the override was edited for (scope_intent key). */
  rewritePromptOverrideKey?: string;
  /** Inline Stage-3 tuning: assembly template + the case key it was edited for. */
  assemblyTemplateOverride?: string;
  assemblyTemplateOverrideKey?: string;
}

interface RegenerationModalProps {
  onClose: () => void;
  onConfirm: (
    feedback: string | null,
    isRefinement: boolean,
    boxCoords?: BoxCoordinates,
    override?: RefinementOverride,
    forceEdit?: boolean,
  ) => void;
  /** Admin prompt-inspector: dry-run the classifier + prompt assembly (no generation). */
  onAnalyze?: (feedback: string, boxCoords?: BoxCoordinates, opts?: AnalyzeOptions) => Promise<StageRefinementPreview>;
  isLoading?: boolean;
  error?: string | null;
  /**
   * Regenerate-suggestion advisory (set by the parent when a refinement returns
   * regenerate_suggested — e.g. a perspective change). Shows a "Neu generieren"
   * + "Trotzdem verfeinern" screen instead of the edit result.
   */
  regenerateSuggestion?: { reason: string | null } | null;
  hasCurrentImage?: boolean;
  currentImageUrl?: string;
  initialMode?: 'choose' | 'feedback';
}

// The Stage-1 classifier prompt has a single fixed key. The Stage-2 rewrite
// prompt is PER-CASE (stage_reprompt_rewrite_${scope}_${intent}) — its key comes
// from the analysis (preview.rewriteTemplateKey) and drives Save + the
// case-tagged override. Both save straight back to prompt_templates (same table
// Admin → Prompts writes to).
const CLASSIFIER_PROMPT_KEY = 'stage_reprompt_classifier';

export const RegenerationModal: React.FC<RegenerationModalProps> = ({
  onClose,
  onConfirm,
  onAnalyze,
  isLoading = false,
  error = null,
  regenerateSuggestion = null,
  hasCurrentImage = false,
  currentImageUrl,
  initialMode = 'choose',
}) => {
  const [mode, setMode] = useState<'choose' | 'feedback'>(initialMode);
  const [feedback, setFeedback] = useState('');
  const [boxCoords, setBoxCoords] = useState<BoxCoordinates | null>(null);

  // Admin prompt-inspector state
  const [preview, setPreview] = useState<StageRefinementPreview | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // Inline Stage-1/Stage-2 prompt tuning (admin). Seeded from the first
  // analysis; edited values are sent back as overrides on every re-analysis.
  const [stagePromptsSeeded, setStagePromptsSeeded] = useState(false);
  const [classifierPrompt, setClassifierPrompt] = useState('');
  const [rewritePrompt, setRewritePrompt] = useState('');
  // Which per-case rewrite prompt is currently loaded (stage_reprompt_rewrite_${scope}_${intent}).
  const [rewriteKey, setRewriteKey] = useState('');
  // Stage-3 assembly template (the recipe) + which per-case key it belongs to.
  const [assemblyTemplate, setAssemblyTemplate] = useState('');
  const [assemblyKey, setAssemblyKey] = useState('');
  const [stageOpen, setStageOpen] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<{ key: string; ok: boolean; text: string } | null>(null);

  const { isAdmin } = useAuth();
  const { settings } = useAppSettings();

  // Editor visible only in feedback mode when the feature is unlocked and an image URL is provided
  const showEditor =
    (isAdmin ? settings.stage_refinement_admin : settings.stage_refinement_live) &&
    !!currentImageUrl &&
    mode === 'feedback';

  // A stale analysis (input changed since) must not be sent — reset it. The
  // tuned stage prompts intentionally survive so the admin keeps iterating on
  // them across different feedback strings.
  const resetPreview = () => { if (preview) { setPreview(null); setAnalyzeError(null); } };

  const handleReroll = () => {
    onConfirm(null, false); // No feedback, not refinement - complete re-roll from original
  };

  const handleAnalyze = async () => {
    if (!onAnalyze || !feedback.trim()) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      // Once seeded, forward the (possibly edited) stage prompts so the analysis
      // reflects the admin's tuning. The rewrite override is tagged with the case
      // it was edited for (rewriteKey) — the server ignores it if the fresh
      // classification lands on a different case. The first run sends nothing →
      // the server echoes the current prompts back and we seed the editors.
      const opts: AnalyzeOptions | undefined = stagePromptsSeeded
        ? {
            classifierPromptOverride: classifierPrompt,
            rewritePromptOverride: rewritePrompt,
            rewritePromptOverrideKey: rewriteKey,
            assemblyTemplateOverride: assemblyTemplate,
            assemblyTemplateOverrideKey: assemblyKey,
          }
        : undefined;
      const p = await onAnalyze(feedback.trim(), boxCoords ?? undefined, opts);
      setPreview(p);
      setEditedPrompt(p.finalPrompt);
      if (!stagePromptsSeeded) {
        // First analysis — seed all three editors.
        setClassifierPrompt(p.classifierPrompt);
        setRewritePrompt(p.rewritePrompt);
        setRewriteKey(p.rewriteTemplateKey);
        setAssemblyTemplate(p.assemblyTemplate);
        setAssemblyKey(p.templateKey);
        setStagePromptsSeeded(true);
      } else {
        // The classification can flip to another case between analyses. The
        // per-case editors (rewrite + assembly) re-seed from the echoed prompt
        // for the new case (our now-mismatched override was ignored server-side);
        // the classifier prompt (single) stays. Editors whose case is unchanged
        // keep the admin's edits.
        if (p.rewriteTemplateKey !== rewriteKey) {
          setRewritePrompt(p.rewritePrompt);
          setRewriteKey(p.rewriteTemplateKey);
        }
        if (p.templateKey !== assemblyKey) {
          setAssemblyTemplate(p.assemblyTemplate);
          setAssemblyKey(p.templateKey);
        }
      }
    } catch (e) {
      setAnalyzeError(e instanceof Error ? e.message : 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveStagePrompt = async (key: string, content: string) => {
    // Same placeholder guard Admin → Prompts enforces on save — a stage prompt
    // with an unknown {{PLACEHOLDER}} would be silently stripped at runtime.
    const unknown = findUnknownPlaceholders(content, 'stage_refinement');
    if (unknown.length) {
      setSaveMsg({ key, ok: false, text: `Unbekannte Platzhalter: ${unknown.join(', ')}` });
      return;
    }
    setSavingKey(key);
    setSaveMsg(null);
    try {
      const { error: saveErr } = await supabase
        .from('prompt_templates')
        .update({ content })
        .eq('key', key);
      if (saveErr) throw saveErr;
      setSaveMsg({ key, ok: true, text: 'Gespeichert' });
    } catch (e) {
      setSaveMsg({ key, ok: false, text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    } finally {
      setSavingKey(null);
    }
  };

  const handleWithCorrection = () => {
    if (!feedback.trim()) return;
    // If the admin analyzed (and possibly edited) the final prompt, run with that
    // exact prompt + the shown routing, skipping the server-side classifier/template.
    const override: RefinementOverride | undefined = preview
      ? { finalPrompt: editedPrompt, scope: preview.scope, intent: preview.intent }
      : undefined;
    onConfirm(feedback.trim(), hasCurrentImage, boxCoords ?? undefined, override);
  };

  // "Trotzdem verfeinern" from the regenerate-suggestion — re-run the same edit
  // with forceEdit so the server skips the advisory short-circuit.
  const handleForceEdit = () => {
    if (!feedback.trim()) return;
    const override: RefinementOverride | undefined = preview
      ? { finalPrompt: editedPrompt, scope: preview.scope, intent: preview.intent }
      : undefined;
    onConfirm(feedback.trim(), hasCurrentImage, boxCoords ?? undefined, override, true);
  };

  // Reusable editor block for one stage prompt (classifier / rewrite).
  const renderStagePromptEditor = (
    key: string,
    title: string,
    value: string,
    setValue: (v: string) => void,
  ) => (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold text-slate-600">{title}</label>
        <button
          type="button"
          onClick={() => handleSaveStagePrompt(key, value)}
          disabled={savingKey === key}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 disabled:opacity-40"
        >
          {savingKey === key
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : saveMsg?.key === key && saveMsg.ok
              ? <Check className="w-3 h-3 text-green-600" />
              : <Save className="w-3 h-3" />}
          Speichern
        </button>
      </div>
      <textarea
        value={value}
        onChange={(e) => { setValue(e.target.value); if (saveMsg?.key === key) setSaveMsg(null); }}
        rows={5}
        className="w-full p-2 text-[11px] font-mono bg-white text-slate-800 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-primary/40 resize-y"
      />
      {saveMsg?.key === key && (
        <p className={`text-[11px] ${saveMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{saveMsg.text}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className={`bg-white rounded-2xl ${showEditor ? 'max-w-2xl' : 'max-w-lg'} w-full shadow-2xl animate-slide-up flex flex-col max-h-[90vh] overflow-hidden`}>
        {/* Header (fixed) */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-lg text-slate-900">Bild regenerieren</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body (scrolls when tall — image + region + textarea + inspector) */}
        <div className="p-6 overflow-y-auto min-h-0">
          {isLoading ? (
            /* ── Generation in progress ── */
            <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">KI generiert dein Bild…</p>
                <p className="text-xs text-slate-500">Das kann bis zu einer Minute dauern. Bitte warten.</p>
              </div>
            </div>
          ) : regenerateSuggestion ? (
            /* ── Regenerate suggestion (e.g. perspective change) ── */
            <div className="flex flex-col items-center justify-center py-6 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Diese Änderung lässt sich per Bearbeitung kaum umsetzen</p>
                <p className="text-sm text-slate-600 max-w-sm mx-auto">
                  {regenerateSuggestion.reason?.trim()
                    || 'Perspektiv-/Winkel-Änderungen gelingen per Bearbeitung selten. Eine Neu-Generierung übernimmt den Blickwinkel des Originals.'}
                </p>
                <p className="text-xs text-slate-500 mt-2">Neu generieren kostet 1 Credit.</p>
              </div>
              <div className="flex gap-2 mt-1">
                <button onClick={onClose} className="btn-ghost text-sm">
                  Abbrechen
                </button>
                <button onClick={handleReroll} className="btn-primary flex items-center gap-2 text-sm">
                  <Dices className="w-4 h-4" />
                  Neu generieren
                </button>
              </div>
              <button
                type="button"
                onClick={handleForceEdit}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Trotzdem als Bearbeitung versuchen →
              </button>
            </div>
          ) : error ? (
            /* ── Error state — keep modal open so user can retry ── */
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">Generierung fehlgeschlagen</p>
                <p className="text-xs text-slate-500 max-w-xs mx-auto">{error}</p>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={onClose}
                  className="btn-ghost text-sm"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    if (mode === 'feedback' && feedback.trim()) {
                      onConfirm(feedback.trim(), hasCurrentImage, boxCoords ?? undefined);
                    } else {
                      onConfirm(null, false);
                    }
                  }}
                  className="btn-primary flex items-center gap-2 text-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  Erneut versuchen
                </button>
              </div>
            </div>
          ) : mode === 'choose' ? (
            <>
              <p className="text-sm text-slate-600 mb-6">
                Wähle, wie du eine neue Version generieren möchtest:
              </p>

              {/* Option Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Option A: Re-roll */}
                <button
                  onClick={handleReroll}
                  className="group p-5 bg-gradient-to-br from-slate-50 to-slate-100 hover:from-primary/5 hover:to-primary/10 border-2 border-slate-200 hover:border-primary/50 rounded-xl text-left transition-all"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow mb-3">
                    <Dices className="w-6 h-6 text-slate-600 group-hover:text-primary transition-colors" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">Neu generieren</h4>
                  <p className="text-xs text-slate-500">
                    Gleicher Prompt, neues Ergebnis. Ideal wenn nur die Umsetzung nicht passt.
                  </p>
                </button>

                {/* Option B: With Correction */}
                <button
                  onClick={() => setMode('feedback')}
                  className="group p-5 bg-gradient-to-br from-slate-50 to-slate-100 hover:from-amber-50 hover:to-amber-100 border-2 border-slate-200 hover:border-amber-300 rounded-xl text-left transition-all"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow mb-3">
                    <Wand2 className="w-6 h-6 text-slate-600 group-hover:text-amber-600 transition-colors" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">Mit Korrektur</h4>
                  <p className="text-xs text-slate-500">
                    {hasCurrentImage
                      ? 'Verfeinere das aktuelle Ergebnis mit deinen Anweisungen.'
                      : 'Generiere neu mit spezifischen Anweisungen.'
                    }
                  </p>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Feedback Mode */}
              <button
                onClick={() => setMode('choose')}
                className="text-sm text-primary hover:text-primary/80 font-medium mb-4 flex items-center gap-1"
              >
                ← Zurück
              </button>

              {/* Region editor — only for admins / when flag is enabled */}
              {showEditor && (
                <div className="mb-4">
                  <RegionSelector
                    imageUrl={currentImageUrl!}
                    box={boxCoords}
                    onBoxChange={(b) => { setBoxCoords(b); resetPreview(); }}
                  />
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    {boxCoords ? (
                      <p className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        Der markierte Bereich wird als Ortsangabe an die KI übergeben.
                      </p>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs text-slate-500">
                        <ScanLine className="w-3.5 h-3.5 shrink-0" />
                        Zieh einen Rahmen um den Bereich, den du ändern willst — optional.
                      </p>
                    )}
                    {boxCoords && (
                      <button
                        type="button"
                        onClick={() => setBoxCoords(null)}
                        className="shrink-0 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-lg transition-colors"
                      >
                        <X className="w-3 h-3" />
                        Auswahl entfernen
                      </button>
                    )}
                  </div>
                </div>
              )}

              <label className="block text-sm font-medium text-slate-700 mb-2">
                {hasCurrentImage ? 'Verfeinerung' : 'Korrektur-Anweisungen'}
              </label>
              {hasCurrentImage && (
                <p className="text-xs text-amber-600 mb-2 bg-amber-50 px-3 py-2 rounded-lg">
                  💡 Die KI arbeitet auf dem aktuellen Bild weiter und ändert nur, was du beschreibst.
                </p>
              )}
              <textarea
                className="w-full p-3 bg-white text-slate-900 border border-slate-300 rounded-xl mb-4 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none placeholder:text-slate-400"
                rows={4}
                placeholder={hasCurrentImage
                  ? "z.B. 'Entferne die Lampe links', 'Ändere das Sofa zu einem helleren Modell'..."
                  : "z.B. 'Entferne die Artefakte an der Decke', 'Hellere Beleuchtung'..."
                }
                value={feedback}
                onChange={(e) => { setFeedback(e.target.value); resetPreview(); }}
                autoFocus
              />

              {/* Admin prompt-inspector: see the routing class + final prompt, edit it, run with it */}
              {isAdmin && onAnalyze && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                      <FlaskConical className="w-3.5 h-3.5" /> Prompt-Analyse (Admin)
                    </span>
                    <button
                      type="button"
                      onClick={handleAnalyze}
                      disabled={!feedback.trim() || analyzing}
                      className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-40"
                    >
                      {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                      Analysieren
                    </button>
                  </div>

                  {analyzeError && <p className="mt-2 text-xs text-red-500">{analyzeError}</p>}

                  {preview && (
                    <div className="mt-3 space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary-600">
                          {preview.scope === 'local' ? 'lokal · Region' : 'global · ganzes Bild'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-700">
                          {preview.intent === 'structural' ? 'strukturell' : 'kosmetisch'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                          Konfidenz {Math.round(preview.confidence * 100)}%
                        </span>
                        {preview.target && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                            Ziel: {preview.target}
                          </span>
                        )}
                        {preview?.region_description && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                            Ort (KI): {preview.region_description}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        → {preview.provider === 'openai' ? 'GPT Image 2' : 'Gemini'} · <code className="text-slate-600">{preview.model}</code> · Template <code className="text-slate-600">{preview.templateKey}</code>
                      </p>
                      {preview.final_action === 'regenerate' && (
                        <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                          ⚠️ Neu-Generierung empfohlen statt Edit{preview.regenerate_reason ? `: ${preview.regenerate_reason}` : ''} — „Verfeinern" würde dem User die Empfehlung zeigen.
                        </p>
                      )}

                      {/* Stage-1 / Stage-2 prompt tuning — collapsible so it doesn't dominate */}
                      <div className="rounded-lg border border-slate-200 bg-white">
                        <button
                          type="button"
                          onClick={() => setStageOpen((v) => !v)}
                          className="flex items-center gap-1.5 w-full px-2.5 py-1.5 text-[11px] font-semibold text-slate-600"
                        >
                          {stageOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          Stufen-Prompts live tunen (Classifier + Rewrite)
                        </button>
                        {stageOpen && (
                          <div className="px-2.5 pb-2.5 space-y-3">
                            <p className="text-[11px] text-slate-400">
                              Ändere einen Prompt und klicke „Analysieren", um die Wirkung sofort im „Finalen Prompt" unten zu sehen. „Speichern" schreibt ihn dauerhaft in die Prompt-Bibliothek. Rewrite + Assembly gelten nur für den aktuellen Fall ({preview.scope === 'local' ? 'lokal' : 'global'} · {preview.intent === 'structural' ? 'strukturell' : 'kosmetisch'}); der Classifier ist fallübergreifend.
                            </p>
                            {renderStagePromptEditor(CLASSIFIER_PROMPT_KEY, 'Stufe 1 · Classifier-Prompt', classifierPrompt, setClassifierPrompt)}
                            {renderStagePromptEditor(
                              rewriteKey,
                              `Stufe 2 · Rewrite-Prompt (${preview.scope === 'local' ? 'lokal' : 'global'} · ${preview.intent === 'structural' ? 'strukturell' : 'kosmetisch'})`,
                              rewritePrompt,
                              setRewritePrompt,
                            )}
                            {renderStagePromptEditor(
                              assemblyKey,
                              `Stufe 3 · Assembly-Template (${preview.scope === 'local' ? 'lokal' : 'global'} · ${preview.intent === 'structural' ? 'strukturell' : 'kosmetisch'})`,
                              assemblyTemplate,
                              setAssemblyTemplate,
                            )}
                          </div>
                        )}
                      </div>

                      {preview.rewrittenPrompt && (
                        <p className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1.5">
                          <span className="font-semibold text-slate-600">Rewrite (Stage 2):</span> {preview.rewrittenPrompt}
                        </p>
                      )}
                      <label className="block text-[11px] font-medium text-slate-600">
                        Finaler Prompt (editierbar — geht exakt so ans Bild-Modell):
                      </label>
                      <textarea
                        value={editedPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        rows={5}
                        className="w-full p-2 text-xs font-mono bg-white text-slate-800 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                      />
                      <p className="text-[11px] text-slate-400">
                        „Verfeinern" nutzt jetzt diesen Prompt (Klassifizierung wird übersprungen).
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="btn-ghost"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleWithCorrection}
                  className="btn-primary flex items-center gap-2"
                  disabled={!feedback.trim()}
                >
                  <Wand2 className="w-4 h-4" />
                  {hasCurrentImage ? 'Verfeinern' : 'Korrigieren'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
